import {Simulations} from "@/components/layouts/main/Simulations";
import {ChatGameEntry} from "@/pages/common/ChatGame/ChatGameEntry";
import {Privacy} from "@/pages/common/Policy/Privacy";
import {Terms} from "@/pages/common/Policy/Terms";

import React from "react";

import {ProfileLayout} from "@layouts/page/ProfileLayout";
import {CriteriaCreatePage} from "@pages/admin/CriteriaPage/CriteriaCreatePage";
import {CriteriaEditPage} from "@pages/admin/CriteriaPage/CriteriaEditPage";
import {CriteriaTablePage} from "@pages/admin/CriteriaPage/CriteriaTablePage";
import {CriteriaViewPage} from "@pages/admin/CriteriaPage/CriteriaViewPage";
import {DashboardPage} from "@pages/admin/DashboardPage";
import {DoctorCreatePage} from "@pages/admin/DoctorPage/DoctorCreatePage";
import {DoctorTablePage} from "@pages/admin/DoctorPage/DoctorTablePage";
import {DoctorViewPage} from "@pages/admin/DoctorPage/DoctorViewPage";
import {ProductCreatePage} from "@pages/admin/ProductPage/ProductCreatePage";
import {ProductEditPage} from "@pages/admin/ProductPage/ProductEditPage";
import {ProductTablePage} from "@pages/admin/ProductPage/ProductTablePage";
import {ProductViewPage} from "@pages/admin/ProductPage/ProductViewPage";
import {ScenarioCreatePage} from "@pages/admin/ScenarioPage/ScenarioCreatePage";
import {ScenarioEditPage} from "@pages/admin/ScenarioPage/ScenarioEditPage";
import {ScenarioTablePage} from "@pages/admin/ScenarioPage/ScenarioTablePage";
import {ScenarioViewPage} from "@pages/admin/ScenarioPage/ScenarioViewPage";
import {SessionTablePage} from "@pages/admin/SessionPage/SessionTablePage";
import {UserCreatePage} from "@pages/admin/UserPage/UserCreatePage";
import {UserEditPage} from "@pages/admin/UserPage/UserEditPage";
import {UserTablePage} from "@pages/admin/UserPage/UserTablePage";
import {VideoCreatePage} from "@pages/admin/VideoPage/VideoCreatePage";
import {VideoEditPage} from "@pages/admin/VideoPage/VideoEditPage";
import {VideoTablePage} from "@pages/admin/VideoPage/VideoTablePage";
import {VideoViewPage} from "@pages/admin/VideoPage/VideoViewPage";
import {SessionViewPage} from "@pages/common/SessionPage/SessionViewPage";
import {SessionTablePage as UserSessionTablePage} from "@pages/user/SessionPage/SessionTablePage";
import {Spinner} from "@ui/general/Spinner";

export const lazyComponents = {
  Dashboard: React.lazy(() => Promise.resolve({default: DashboardPage})),
  Users: React.lazy(() => Promise.resolve({default: UserTablePage})),
  Products: React.lazy(() => Promise.resolve({default: ProductTablePage})),
  Scenarios: React.lazy(() => Promise.resolve({default: ScenarioTablePage})),
  Library: React.lazy(() => Promise.resolve({default: VideoTablePage})),
  AdminAnalytics: React.lazy(() =>
    Promise.resolve({default: SessionTablePage}),
  ),
  UserAnalytics: React.lazy(() =>
    Promise.resolve({default: UserSessionTablePage}),
  ),
  Privacy: React.lazy(() => Promise.resolve({default: Privacy})),
  Terms: React.lazy(() => Promise.resolve({default: Terms})),
  Simulations: React.lazy(() => Promise.resolve({default: Simulations})),
  ChatGameEntry: React.lazy(() => Promise.resolve({default: ChatGameEntry})),
  ProfileStatisticLayout: React.lazy(() =>
    Promise.resolve({default: ProfileLayout}),
  ),
  UserCreateLayout: React.lazy(() =>
    Promise.resolve({default: UserCreatePage}),
  ),
  UserEditLayout: React.lazy(() => Promise.resolve({default: UserEditPage})),
  UserViewLayout: React.lazy(() => Promise.resolve({default: ProfileLayout})),
  ProductViewLayout: React.lazy(() =>
    Promise.resolve({default: ProductViewPage}),
  ),
  ProductEditLayout: React.lazy(() =>
    Promise.resolve({default: ProductEditPage}),
  ),
  ProductCreateLayout: React.lazy(() =>
    Promise.resolve({default: ProductCreatePage}),
  ),
  ScenarioViewLayout: React.lazy(() =>
    Promise.resolve({default: ScenarioViewPage}),
  ),
  ScenarioCreateLayout: React.lazy(() =>
    Promise.resolve({default: ScenarioCreatePage}),
  ),
  ScenarioEditLayout: React.lazy(() =>
    Promise.resolve({default: ScenarioEditPage}),
  ),
  VideoViewLayout: React.lazy(() => Promise.resolve({default: VideoViewPage})),
  VideoEditLayout: React.lazy(() => Promise.resolve({default: VideoEditPage})),
  VideoCreateLayout: React.lazy(() =>
    Promise.resolve({default: VideoCreatePage}),
  ),
  SessionViewLayout: React.lazy(() =>
    Promise.resolve({default: SessionViewPage}),
  ),
  Doctors: React.lazy(() => Promise.resolve({default: DoctorTablePage})),
  DoctorViewLayout: React.lazy(() =>
    Promise.resolve({default: DoctorViewPage}),
  ),
  DoctorCreateLayout: React.lazy(() =>
    Promise.resolve({default: DoctorCreatePage}),
  ),
  CriteriaTableLayout: React.lazy(() =>
    Promise.resolve({default: CriteriaTablePage}),
  ),
  CriteriaCreateLayout: React.lazy(() =>
    Promise.resolve({default: CriteriaCreatePage}),
  ),
  CriteriaViewLayout: React.lazy(() =>
    Promise.resolve({default: CriteriaViewPage}),
  ),
  CriteriaEditLayout: React.lazy(() =>
    Promise.resolve({default: CriteriaEditPage}),
  ),
};

export const createSuspenseWrapper = (
  Component,
  fallback = (
    <Spinner
      width={80}
      height={80}
    />
  ),
) => {
  return (
    <React.Suspense fallback={fallback}>
      <Component />
    </React.Suspense>
  );
};

export const adminRoutes = [
  {
    path: "dashboard/*",
    component: lazyComponents.Dashboard,
  },
  {
    path: "profile/:id",
    component: lazyComponents.ProfileStatisticLayout,
  },
  {
    path: "users/*",
    component: lazyComponents.Users,
  },
  {
    path: "users/view/:id",
    component: lazyComponents.UserViewLayout,
  },
  {
    path: "users/edit/:id",
    component: lazyComponents.UserEditLayout,
  },
  {
    path: "users/create",
    component: lazyComponents.UserCreateLayout,
  },
  {
    path: "products/*",
    component: lazyComponents.Products,
  },
  {
    path: "products/view/:id",
    component: lazyComponents.ProductViewLayout,
  },
  {
    path: "products/edit/:id",
    component: lazyComponents.ProductEditLayout,
  },
  {
    path: "products/create",
    component: lazyComponents.ProductCreateLayout,
  },
  {
    path: "call/*",
    component: lazyComponents.Scenarios,
  },
  {
    path: "call/view/:id",
    component: lazyComponents.ScenarioViewLayout,
  },
  {
    path: "call/edit/:id",
    component: lazyComponents.ScenarioEditLayout,
  },
  {
    path: "call/create",
    component: lazyComponents.ScenarioCreateLayout,
  },
  {
    path: "videos/*",
    component: lazyComponents.Library,
  },
  {
    path: "videos/view/:id",
    component: lazyComponents.VideoViewLayout,
  },
  {
    path: "videos/edit/:id",
    component: lazyComponents.VideoEditLayout,
  },
  {
    path: "videos/create",
    component: lazyComponents.VideoCreateLayout,
  },
  {
    path: "criteria/*",
    component: lazyComponents.CriteriaTableLayout,
  },
  {
    path: "criteria/view/:id",
    component: lazyComponents.CriteriaViewLayout,
  },
  {
    path: "criteria/create",
    component: lazyComponents.CriteriaCreateLayout,
  },
  {
    path: "criteria/edit/:id",
    component: lazyComponents.CriteriaEditLayout,
  },
  {
    path: "analytics/product/*",
    component: lazyComponents.AdminAnalytics,
  },
  {
    path: "analytics/scenario/*",
    component: lazyComponents.AdminAnalytics,
  },
  {
    path: "analytics/certification/*",
    component: lazyComponents.AdminAnalytics,
  },
  {
    path: "analytics/scenario/view/:id",
    component: lazyComponents.SessionViewLayout,
  },
  {
    path: "analytics/product/view/:id",
    component: lazyComponents.SessionViewLayout,
  },
  {
    path: "analytics/certification/view/:id",
    component: lazyComponents.SessionViewLayout,
  },
  {
    path: "certifications/*",
    component: lazyComponents.Certifications,
  },
  {
    path: "certifications/scenarios/*",
    component: lazyComponents.Certifications,
  },
  {
    path: "certifications/create",
    component: lazyComponents.CertificationCreateLayout,
  },
  {
    path: "certifications/view/:id",
    component: lazyComponents.CertificationViewLayout,
  },
  {
    path: "certifications/scenarios/create",
    component: lazyComponents.CertificationScenarioCreateLayout,
  },
  {
    path: "certifications/scenarios/view/:id",
    component: lazyComponents.CertificationScenarioViewLayout,
  },
  {
    path: "doctors/view/:id",
    component: lazyComponents.DoctorViewLayout,
  },
  {
    path: "doctors/create",
    component: lazyComponents.DoctorCreateLayout,
  },
  {
    path: "doctors/*",
    component: lazyComponents.Doctors,
  },
];

export const userRoutes = [
  {
    path: "profile/:id",
    component: lazyComponents.ProfileStatisticLayout,
  },
  {
    path: "analytics/scenario/*",
    component: lazyComponents.UserAnalytics,
  },
  {
    path: "analytics/scenario/view/:id",
    component: lazyComponents.SessionViewLayout,
  },
  {
    path: "analytics/product/*",
    component: lazyComponents.UserAnalytics,
  },
  {
    path: "analytics/product/view/:id",
    component: lazyComponents.SessionViewLayout,
  },
  {
    path: "analytics/certification/*",
    component: lazyComponents.UserAnalytics,
  },
  {
    path: "analytics/certification/view/:id",
    component: lazyComponents.SessionViewLayout,
  },
];

export const authenticatedRoutes = [
  {
    path: "/simulations/*",
    component: lazyComponents.Simulations,
  },
  {
    path: "/chat/:session/:level/:id",
    component: lazyComponents.ChatGameEntry,
  },
  {
    path: "/privacy",
    component: lazyComponents.Privacy,
  },
  {
    path: "/terms",
    component: lazyComponents.Terms,
  },
];

export const unauthenticatedRoutes = [
  {
    path: "/privacy",
    component: lazyComponents.Privacy,
  },
  {
    path: "/terms",
    component: lazyComponents.Terms,
  },
];

export const createRoutes = (routesConfig) => {
  return routesConfig.map((route, index) => ({
    path: route.path,
    element: createSuspenseWrapper(route.component),
    key: index,
  }));
};
